import { isEmpty, isFunction } from 'underscore';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { useNeedsAttentionForAdapter } from '@bingads-webui-campaign-react/attention-beacon';

export const FormErrorBeaconAdapter = (WrappedComponent) => {
  const HOC = ({
    formErrorProps: formErrorPropsFromProps,
    attentionBeaconAdapterProps: attentionBeaconAdapterPropsFromProps,
    ...restProps
  }) => {
    const {
      onUnhandledErrorsChange: onUnhandledErrorsChangedFromProps,
      onUnmappedErrorsChange: onUnmappedErrorsChangeFromProps,
    } = formErrorPropsFromProps;
    const [needsAttentionForUnhandled, setNeedsAttentionForUnhandled] = useState(false);
    const onUnhandledErrorsChange = useCallback((unhandledErrors) => {
      if (isFunction(onUnhandledErrorsChangedFromProps)) {
        onUnhandledErrorsChangedFromProps(unhandledErrors);
      }
      setNeedsAttentionForUnhandled(!isEmpty(unhandledErrors));
    }, [onUnhandledErrorsChangedFromProps]);
    const [needsAttentionForUnmapped, setNeedsAttentionForUnmapped] = useState(false);
    const onUnmappedErrorsChange = useCallback((unmappedErrors) => {
      if (isFunction(onUnmappedErrorsChangeFromProps)) {
        onUnmappedErrorsChangeFromProps(unmappedErrors);
      }
      setNeedsAttentionForUnmapped(!isEmpty(unmappedErrors));
    }, [onUnmappedErrorsChangeFromProps]);

    const needsAttentionLocal = needsAttentionForUnhandled || needsAttentionForUnmapped;
    const { attentionBeaconAdapterProps } = useNeedsAttentionForAdapter({ needsAttentionLocal, attentionBeaconAdapterProps: attentionBeaconAdapterPropsFromProps });

    const formErrorProps = {
      ...formErrorPropsFromProps,
      onUnhandledErrorsChange,
      onUnmappedErrorsChange,
    };

    return (
      <WrappedComponent
        {...restProps}
        formErrorProps={formErrorProps}
        {...WrappedComponent.isAttentionBeaconAdapter ? { attentionBeaconAdapterProps } : {}}
      />
    );
  };

  HOC.propTypes = {
    formErrorProps: PropTypes.shape({
      onUnhandledErrorsChange: PropTypes.func,
      onUnmappedErrorsChange: PropTypes.func,
    }),
    attentionBeaconAdapterProps: PropTypes.shape({
      onAdapterNeedsAttentionChange: PropTypes.func.isRequired,
    }).isRequired,
  };

  HOC.defaultProps = {
    formErrorProps: {},
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `FormErrorBeaconAdapter(${getDisplayName(WrappedComponent)})`;
  HOC.isAttentionBeaconAdapter = true;

  return HOC;
};
